import {PerformanceData} from "../PerformanceChartReducer";
import {truncateNumber} from "../../../../Utils/NumberUtil";
import {formatAvailability, formatPerformanceRatio} from "../../../../Utils/PerformanceRatioUtils";
import {calculateProgress} from "./PerformanceDisplayUtils";

export type PerformanceSortCriterion =
    | "performanceRatio"
    | "generatedEnergyPercentage"
    | "irradiancePercentage"
    | "availability"
    | "powerStationName";

export interface PerformanceSortOption {
    value: PerformanceSortCriterion;
    label: string;
}

export const DEFAULT_PERFORMANCE_SORT_CRITERION: PerformanceSortCriterion = "performanceRatio";

export const PERFORMANCE_SORT_OPTIONS: readonly PerformanceSortOption[] = [
    {value: "performanceRatio", label: "PR"},
    {value: "generatedEnergyPercentage", label: "Energia gerada"},
    {value: "irradiancePercentage", label: "Irradiação"},
    {value: "availability", label: "Disponibilidade"},
    {value: "powerStationName", label: "Nome da Usina"},
];

const PERFORMANCE_SORT_CRITERION_SET = new Set<string>(
    PERFORMANCE_SORT_OPTIONS.map((option) => option.value)
);

export const isPerformanceSortCriterion = (value: unknown): value is PerformanceSortCriterion =>
    typeof value === "string" && PERFORMANCE_SORT_CRITERION_SET.has(value);

export const parsePerformanceSortCriterion = (
    value: unknown
): PerformanceSortCriterion | null =>
    isPerformanceSortCriterion(value) ? value : null;

const parsePresentedPercent = (formatted: string): number => {
    if (formatted === "-") {
        return 0;
    }
    const parsed = Number(formatted.replace(",", "."));
    return Number.isFinite(parsed) ? parsed : 0;
};

const isLoadingCard = (card: PerformanceData): boolean => card.loading === true;

const comparePlantNames = (left: PerformanceData, right: PerformanceData): number =>
    left.powerStationName.localeCompare(right.powerStationName, "pt-BR");

export const extractSortValue = (
    card: PerformanceData,
    criterion: PerformanceSortCriterion
): number | string => {
    if (criterion === "powerStationName") {
        return card.powerStationName;
    }
    // Erro ou usina sem retorno no lote (SGD-3904 RN04): sem valor, conta como zero.
    if (card.error || card.noData) {
        return 0;
    }
    if (criterion === "generatedEnergyPercentage") {
        return calculateProgress(card.generatedEnergy, card.expectedInjectedEnergy) ?? 0;
    }
    if (criterion === "irradiancePercentage") {
        return calculateProgress(card.irradiance, card.expectedIrradiance) ?? 0;
    }
    if (criterion === "availability") {
        return parsePresentedPercent(formatAvailability(card.availability));
    }
    return parsePresentedPercent(
        formatPerformanceRatio(truncateNumber(card.performanceRatio, 2))
    );
};

export const comparePerformanceCards = (
    left: PerformanceData,
    right: PerformanceData,
    criterion: PerformanceSortCriterion
): number => {
    const leftLoading = isLoadingCard(left);
    const rightLoading = isLoadingCard(right);
    if (leftLoading !== rightLoading) {
        return leftLoading ? 1 : -1;
    }
    if (leftLoading) {
        return 0;
    }
    if (criterion === "powerStationName") {
        return comparePlantNames(left, right);
    }
    const leftValue = extractSortValue(left, criterion) as number;
    const rightValue = extractSortValue(right, criterion) as number;
    if (leftValue !== rightValue) {
        return leftValue - rightValue;
    }
    return comparePlantNames(left, right);
};

export const sortPerformanceCards = (
    cards: readonly PerformanceData[],
    criterion: PerformanceSortCriterion
): PerformanceData[] =>
    [...cards].sort((left, right) => comparePerformanceCards(left, right, criterion));

export const areIdOrdersEqual = (left: readonly number[], right: readonly number[]): boolean =>
    left.length === right.length && left.every((id, index) => id === right[index]);

export const reconcileCommittedCardOrder = (
    committedOrder: readonly number[],
    visibleIds: readonly number[]
): number[] => {
    if (visibleIds.length === 0) {
        return [];
    }
    const visibleSet = new Set(visibleIds);
    const seen = new Set<number>();
    const retained: number[] = [];
    committedOrder.forEach((id) => {
        if (visibleSet.has(id) && !seen.has(id)) {
            seen.add(id);
            retained.push(id);
        }
    });
    const newcomers = visibleIds.filter((id) => !seen.has(id));
    return [...retained, ...newcomers];
};

export const projectCardsByCommittedOrder = (
    cards: readonly PerformanceData[],
    committedOrder: readonly number[]
): PerformanceData[] => {
    const byId = new Map(cards.map((card) => [card.id, card]));
    const projected: PerformanceData[] = [];
    committedOrder.forEach((id) => {
        const card = byId.get(id);
        if (card) {
            projected.push(card);
        }
    });
    return projected;
};
